import {
  FormLabel,
  FormControl,
  Stack,
  Divider,
  Box,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { AccountState, type Account } from '@repo/api-gw-sdk';

import { BulletItem } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/bulletItem';
import { NumericBulletSection } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/numericBulletSection';
import type { BackupVault } from '@/data/vaults/backupVault';

import { VaultOwnBucketCopyableAllAccountsPolicy } from './vaultOwnBucketCopyablePolicy';
import { VaultOwnBucketEditKeyPolicyButton } from './vaultOwnBucketEditKeyPolicyButton';

import { BottomDialog } from '../dialogs/bottomDialog';
import { PanelWrapper } from '../panels/panelWrapper';
import { useBuckets } from '../restore/useBuckets';
import { SelectPlaceholder } from '../selectPlaceholder';

export const SelectVaultBucketPanel = ({
  open,
  onClose,
  disabled,
  data,
  setData,
  vaultAccounts,
}: {
  open: boolean;
  onClose: () => void;
  disabled: boolean;
  data: BackupVault;
  setData: (data: BackupVault) => void;
  vaultAccounts: Account[];
}) => {
  return (
    <BottomDialog open={open} isFullScreen={true}>
      <PanelWrapper
        header={{
          title: 'Select bucket',
          subtitle:
            'Select a bucket from your self-managed vault account. You’re responsible for the vault’s security, availability, and ensuring Eon has access to manage snapshots.',
          fullWidthSubtitle: false,
          onClose: onClose,
        }}
        footer={{
          primaryButton: {
            text: 'Done',
            'data-testid': 'done-button',
            onClick: onClose,
          },
          secondaryButton: {
            text: 'Cancel',
            'data-testid': 'cancel-button',
            onClick: onClose,
          },
        }}
      >
        <Box padding='40px min(100px,6vw)' width='100%' height='auto'>
          <Stack
            gap='40px'
            divider={<Divider sx={{ borderStyle: 'dashed' }} />}
          >
            <NumericBulletSection
              bulletNumber={1}
              title='Select vault account and bucket'
            >
              <BulletItem
                leftContent={
                  <Stack gap='24px'>
                    <VaultAccountSelector
                      disabled={disabled}
                      vaultAccountId={data.vaultAccountId || ''}
                      setAccountId={(vaultAccountId) => {
                        setData({
                          ...data,
                          vaultAccountId,
                          vaultAttributes: {
                            ...data.vaultAttributes,
                            aws: undefined,
                          },
                        });
                      }}
                      vaultAccounts={vaultAccounts}
                    />
                    <BucketSelector
                      account={
                        vaultAccounts.find(
                          (account) => account.id === data.vaultAccountId
                        ) || ({} as Account)
                      }
                      region={data.region || ''}
                      disabled={disabled}
                      bucketName={data.vaultAttributes.aws?.bucketName || ''}
                      setBucketName={(bucketName) => {
                        setData({
                          ...data,
                          vaultAttributes: {
                            ...data.vaultAttributes,
                            aws: { ...data.vaultAttributes.aws, bucketName },
                          },
                        });
                      }}
                    />
                  </Stack>
                }
              />
            </NumericBulletSection>
            <NumericBulletSection bulletNumber={2} title='Add policy in AWS'>
              <BulletItem
                title='Review and add this policy in your AWS account. Afterward, come back to this tab to finish the connection.'
                leftContent={
                  <VaultOwnBucketEditKeyPolicyButton
                    region={data.region}
                    bucketName={data.vaultAttributes.aws?.bucketName || ''}
                  />
                }
                rightContent={
                  <VaultOwnBucketCopyableAllAccountsPolicy
                    bucketName={data.vaultAttributes.aws?.bucketName || ''}
                  />
                }
              />
            </NumericBulletSection>
          </Stack>
        </Box>
      </PanelWrapper>
    </BottomDialog>
  );
};

const BucketSelector = ({
  disabled,
  bucketName,
  setBucketName,
  account,
  region,
}: {
  disabled: boolean;
  bucketName: string;
  setBucketName: (bucketName: string) => void;
  account: Account;
  region: string;
}) => {
  const { buckets, isLoading } = useBuckets(account, region);

  return (
    <FormControl size='small' className='w-full px-[0px]'>
      <FormLabel>Bucket</FormLabel>
      <Select
        disabled={disabled || isLoading}
        size='small'
        value={bucketName || ''}
        inputProps={{
          'data-testid': 'vault-bucket-name',
        }}
        displayEmpty
        renderValue={(value: string) => {
          if (!value) {
            return <SelectPlaceholder />;
          }
          return value;
        }}
        onChange={(event) => {
          setBucketName(event.target.value);
        }}
      >
        {buckets.map((bucket) => (
          <MenuItem key={bucket.bucketName} value={bucket.bucketName}>
            {bucket.bucketName}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const VaultAccountSelector = ({
  disabled,
  vaultAccountId,
  setAccountId,
  vaultAccounts,
}: {
  disabled: boolean;
  vaultAccountId: string;
  setAccountId: (vaultAccountId: string) => void;
  vaultAccounts: Account[];
}) => {
  return (
    <FormControl size='small' className='w-full px-[0px]'>
      <FormLabel>Vault account</FormLabel>
      <Select
        disabled={disabled}
        size='small'
        value={vaultAccountId || ''}
        inputProps={{
          'data-testid': 'vault-bucket-name',
        }}
        displayEmpty
        renderValue={(value: string) => {
          if (!value) {
            return <SelectPlaceholder />;
          }
          return (
            vaultAccounts.find((account) => account.id === value)?.name || ''
          );
        }}
        onChange={(event) => {
          if (event.target.value === vaultAccountId || !event.target.value) {
            return;
          }
          setAccountId(event.target.value);
        }}
      >
        {vaultAccounts.map((account) => (
          <Tooltip
            key={account.id}
            title={
              account.state === AccountState.ACCOUNT_STATE_DISCONNECTED
                ? 'Disconnected'
                : ''
            }
            placement='right'
          >
            <span>
              <MenuItem
                value={account.id}
                disabled={
                  account.state === AccountState.ACCOUNT_STATE_DISCONNECTED
                }
              >
                {account.name}
              </MenuItem>
            </span>
          </Tooltip>
        ))}
      </Select>
    </FormControl>
  );
};
